/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import CodeEditorError from 'DesignData/models/validation/CodeEditorError';
import { ERROR, WARNING, INFO } from 'DesignData/enums/EditorErrorTypes';
import ReactCodeMirrorAnnotationType from 'react-codemirror/enum/AnnotationType';
const ReactCodeMirrorAnnotationTypeToEditorErrorType = {
  [ReactCodeMirrorAnnotationType.ERROR]: ERROR,
  [ReactCodeMirrorAnnotationType.WARNING]: WARNING,
  [ReactCodeMirrorAnnotationType.INFO]: INFO
};
/**
 * Attempts to determine the line, column, and evidence of a SyntaxError.
 *
 * @param {SyntaxError} error
 * @param {String}      source Code that caused the syntax error
 * @returns {Object}
 */

export function parseLineAndColumnFromSyntaxError(error, source) {
  let lineNumber = 0;
  let column = 0;
  let evidence = '';

  if (!(error instanceof SyntaxError && source)) {
    return {
      lineNumber,
      column,
      evidence
    };
  }

  if (error.lineNumber != null && error.columnNumber != null) {
    // Firefox
    lineNumber = error.lineNumber;
    column = error.columnNumber;
  } else {
    // Chrome
    const positionMatch = error.message.match(/at position (\d+)/i);
    const position = positionMatch && positionMatch[1] | 0;

    if (position > 0) {
      for (let i = 0; i < position && i < source.length; ++i) {
        const ch = source[i];

        if (ch === '\n') {
          ++lineNumber;
          column = 0;
        } else {
          ++column;
        }
      }
    }

    const evidenceMatch = error.message.match(/Unexpected token (.+) in JSON/i);
    evidence = evidenceMatch && evidenceMatch[1] || evidence;
  }

  return {
    lineNumber,
    column,
    evidence
  };
}
/**
 * Super simplistic JSON code validation.
 *
 * @param {String} jsonCode JSON code string
 * @returns {SyntaxError|Null} Returns a SyntaxError if invalid, null otherwise.
 */

export function parseJsonForSyntaxError(jsonCode) {
  if (!jsonCode) {
    return null;
  }

  let error = null;

  try {
    JSON.parse(jsonCode);
  } catch (e) {
    error = e;
  }

  return error;
}
/**
 * Creates a CodeEditorError from a JS Error.
 *
 * @param {Error}   error
 * @param {String}  source
 * @param {Object}  extraFields
 * @returns {CodeEditorError|Null}
 */

export function convertJSONParseErrorToCodeEditorError(error, source, extraFields = {}) {
  if (!(error instanceof Error)) {
    return null;
  }

  const message = `${error.name}: ${error.message}`;
  const {
    lineNumber,
    column,
    evidence
  } = parseLineAndColumnFromSyntaxError(error, source);
  return new CodeEditorError(Object.assign({
    lineNumber,
    column,
    evidence,
    message,
    rawMessage: message,
    type: ERROR
  }, extraFields));
}
/**
 * Validates JSON for Code Editor errors.
 *
 * @param {string} eslintConfig JSON source
 * @param {string|number} sessionId CodeMirror sessionId to associate errors with.
 * @returns {CodeEditorError|Null} Returns a CodeEditorError if invalid, null otherwise.
 */

export function validateJSON(source, sessionId) {
  const syntaxError = parseJsonForSyntaxError(source);
  if (!syntaxError) return null;
  return convertJSONParseErrorToCodeEditorError(syntaxError, source, {
    sessionId
  });
}
/**
 * Creates a CodeEditorError from a react-codemirror Annotation model instance.
 *
 * @param {react-codemirror/model/Annotation} annotation
 * @param {String|Number}                     sessionId
 * @param {Object}                            extraFields
 */

export function convertReactCodeMirrorAnnotationToCodeEditorError(annotation, sessionId, extraFields = {}) {
  return new CodeEditorError(Object.assign({}, annotation, {
    sessionId,
    type: ReactCodeMirrorAnnotationTypeToEditorErrorType[annotation.type],
    rawMessage: annotation.message
  }, extraFields));
}